import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiShoppingCart, FiUser, FiMenu, FiX, FiSearch } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import BrandLogo from '../common/BrandLogo';

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen]         = useState(false);
  const [searchOpen, setSearchOpen]     = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [searchQuery, setQuery]         = useState('');
  const accountMenuRef = useRef(null);

  useEffect(() => {
    if (!accountMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [accountMenuOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${searchQuery.trim()}`);
      setSearchOpen(false);
      setQuery('');
    }
  };

  const categories = [
    { label: 'Shirts',       href: '/shop/shirts' },
    { label: 'Pants',        href: '/shop/pants' },
    { label: 'Baby Clothes', href: '/shop/baby-clothes' },
    { label: 'Kids',         href: '/shop/kids-clothes' },
    { label: 'Teens',        href: '/shop/teen-clothes' },
  ];

  return (
    <header className="sticky top-0 z-50 bg-yamass-white border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <BrandLogo size="md" imageClassName="h-8 sm:h-9" />

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            {categories.map(c => (
              <Link
                key={c.href}
                to={c.href}
                className="text-sm font-medium text-gray-600 hover:text-yamass-accent transition-colors"
              >
                {c.label}
              </Link>
            ))}
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-4">
            {/* Search */}
            <button onClick={() => setSearchOpen(s => !s)} className="text-gray-600 hover:text-yamass-accent">
              <FiSearch size={20} />
            </button>

            {/* Cart */}
            <Link to="/cart" className="relative text-gray-600 hover:text-yamass-accent">
              <FiShoppingCart size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-yamass-accent text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* User */}
            {user ? (
              <div ref={accountMenuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setAccountMenuOpen(open => !open)}
                  className="flex items-center gap-1 text-gray-600 hover:text-yamass-accent text-sm"
                >
                  <FiUser size={20} />
                  <span className="hidden sm:inline">{user.name.split(' ')[0]}</span>
                </button>
                {accountMenuOpen && (
                  <div className="absolute right-0 top-10 z-20 bg-white border border-gray-100 rounded-lg shadow-lg py-2 w-44">
                    <Link to="/profile" onClick={() => setAccountMenuOpen(false)} className="block px-4 py-2 text-sm hover:bg-yamass-cream">My Profile</Link>
                    <Link to="/orders" onClick={() => setAccountMenuOpen(false)} className="block px-4 py-2 text-sm hover:bg-yamass-cream">My Orders</Link>
                    {isAdmin && <Link to="/admin" onClick={() => setAccountMenuOpen(false)} className="block px-4 py-2 text-sm hover:bg-yamass-cream text-yamass-green font-medium">Admin Panel</Link>}
                  <hr className="my-1" />
                    <button
                      type="button"
                      onClick={() => {
                        setAccountMenuOpen(false);
                        logout();
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="text-sm font-medium bg-yamass-black text-white px-4 py-2 rounded-full hover:bg-yamass-accent transition-colors">
                Login
              </Link>
            )}

            {/* Mobile menu toggle */}
            <button onClick={() => setMenuOpen(s => !s)} className="md:hidden text-gray-600">
              {menuOpen ? <FiX size={22} /> : <FiMenu size={22} />}
            </button>
          </div>
        </div>

        {/* Search bar */}
        {searchOpen && (
          <form onSubmit={handleSearch} className="pb-3">
            <input
              autoFocus
              value={searchQuery}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full border border-gray-200 rounded-full px-5 py-2 text-sm focus:outline-none focus:border-yamass-accent"
            />
          </form>
        )}

        {/* Mobile menu */}
        {menuOpen && (
          <nav className="md:hidden pb-4 border-t border-gray-100 pt-3 flex flex-col gap-3">
            {categories.map(c => (
              <Link
                key={c.href}
                to={c.href}
                onClick={() => setMenuOpen(false)}
                className="text-sm font-medium text-gray-700 hover:text-yamass-accent"
              >
                {c.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}
